<template>
  <div class="social-media-widget" :style="widgetStyles">
    <div v-if="widget.title" class="social-title mb-3">
      <h3 :style="titleStyles">{{ widget.title }}</h3>
    </div>

    <div class="social-links" :class="alignmentClass">
      <a 
        v-for="platform in widget.platforms" 
        :key="platform.id"
        :href="platform.url"
        :target="widget.openInNewTab !== false ? '_blank' : '_self'"
        :rel="widget.openInNewTab !== false ? 'noopener noreferrer' : undefined"
        class="social-link"
        :class="[socialStyleClass, socialSizeClass]"
        :style="socialLinkStyles(platform)"
        :aria-label="`Visit our ${platform.platform} page`"
      >
        <i :class="getSocialIcon(platform.platform)" class="social-icon"></i>
        <span v-if="widget.showLabels" class="social-label">
          {{ platform.label || getPlatformName(platform.platform) }}
        </span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { SocialMediaWidget } from '@/types/pagebuilder'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'

interface Props {
  widget: SocialMediaWidget
}

const props = defineProps<Props>()

// Computed styles
const widgetStyles = computed(() => ({
  padding: props.widget.padding || '1rem 0',
  ...resolveWidgetBackgroundStyle(props.widget),
  textAlign: props.widget.alignment || 'left',
}))

const titleStyles = computed(() => ({
  color: props.widget.titleColor || '#333',
  fontSize: props.widget.titleSize || '1.5rem',
  textAlign: props.widget.titleAlignment || props.widget.alignment || 'left',
  margin: '0 0 1rem 0'
}))

const alignmentClass = computed(() => {
  const alignment = props.widget.alignment || 'left'
  return {
    'justify-content-start': alignment === 'left',
    'justify-content-center': alignment === 'center',
    'justify-content-end': alignment === 'right'
  }
})

const socialStyleClass = computed(() => {
  const style = props.widget.style || 'icons'
  return {
    'social-icons': style === 'icons',
    'social-buttons': style === 'buttons',
    'social-rounded': style === 'rounded'
  }
})

const socialSizeClass = computed(() => {
  const size = props.widget.size || 'medium'
  return {
    'social-small': size === 'small',
    'social-medium': size === 'medium',
    'social-large': size === 'large'
  }
})

// Methods
const getSocialIcon = (platform: string): string => {
  const icons: Record<string, string> = {
    facebook: 'bi bi-facebook',
    twitter: 'bi bi-twitter-x',
    instagram: 'bi bi-instagram',
    linkedin: 'bi bi-linkedin',
    youtube: 'bi bi-youtube',
    tiktok: 'bi bi-tiktok',
    whatsapp: 'bi bi-whatsapp',
    email: 'bi bi-envelope'
  }
  return icons[platform] || 'bi bi-link-45deg'
}

const getPlatformName = (platform: string): string => {
  const names: Record<string, string> = {
    facebook: 'Facebook',
    twitter: 'Twitter',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    whatsapp: 'WhatsApp',
    email: 'Email'
  }
  return names[platform] || platform
}

const getPlatformColor = (platform: string): string => {
  const colors: Record<string, string> = {
    facebook: '#1877f2',
    twitter: '#000000',
    instagram: '#E4405F',
    linkedin: '#0077B5',
    youtube: '#FF0000',
    tiktok: '#000000',
    whatsapp: '#25D366',
    email: '#6c757d'
  }
  return colors[platform] || '#6c757d'
}

const socialLinkStyles = (platform: any) => {
  const baseColor = getPlatformColor(platform.platform)
  const style = props.widget.style || 'icons'
  
  if (style === 'buttons' || style === 'rounded') {
    return {
      backgroundColor: baseColor,
      borderColor: baseColor,
      color: '#ffffff'
    }
  }
  
  return {
    color: baseColor
  }
}
</script>

<style scoped>
.social-media-widget {
  max-width: 100%;
}

.social-title h3 {
  margin: 0;
  font-weight: 600;
}

.social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.social-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
  position: relative;
  overflow: hidden;
}

.social-link::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.social-link:hover::before {
  width: 300px;
  height: 300px;
}

.social-link:hover {
  text-decoration: none;
  transform: translateY(-5px) scale(1.05);
  filter: brightness(1.1);
}

.social-link:active {
  transform: translateY(-2px) scale(1.02);
}

.social-icon {
  display: inline-block;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
}

.social-link:hover .social-icon {
  transform: rotate(360deg) scale(1.2);
}

.social-label {
  margin-left: 0.5rem;
  font-size: 0.9rem;
}

/* Icon Style */
.social-icons {
  color: #6c757d;
  font-size: 1.5rem;
  padding: 0.5rem;
  border-radius: 50%;
  background-color: transparent;
  border: 2px solid transparent;
}

.social-icons:hover {
  background-color: rgba(0, 0, 0, 0.05);
  border-color: currentColor;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Button Style */
.social-buttons {
  padding: 0.75rem 1.25rem;
  border-radius: 0.375rem;
  background-color: #6c757d;
  color: white;
  border: 2px solid transparent;
  font-weight: 500;
}

.social-buttons:hover {
  filter: brightness(1.1);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  animation: bounce 0.6s ease;
}

@keyframes bounce {
  0%, 100% { transform: translateY(-5px) scale(1.05); }
  50% { transform: translateY(-10px) scale(1.08); }
}

.social-buttons .social-icon {
  font-size: 1.1rem;
}

/* Rounded Style */
.social-rounded {
  padding: 0.75rem;
  border-radius: 50%;
  background-color: #6c757d;
  color: white;
  border: 2px solid transparent;
  width: 3rem;
  height: 3rem;
  justify-content: center;
  min-width: 3rem;
}

.social-rounded:hover {
  filter: brightness(1.1);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  animation: spin-bounce 0.6s ease;
}

@keyframes spin-bounce {
  0% { transform: translateY(-5px) scale(1.05) rotate(0deg); }
  50% { transform: translateY(-10px) scale(1.1) rotate(180deg); }
  100% { transform: translateY(-5px) scale(1.05) rotate(360deg); }
}

.social-rounded .social-icon {
  font-size: 1.2rem;
}

.social-rounded .social-label {
  display: none;
}

/* Sizes */
.social-small.social-icons {
  font-size: 1.25rem;
  padding: 0.375rem;
}

.social-small.social-buttons {
  padding: 0.5rem 0.875rem;
  font-size: 0.875rem;
}

.social-small.social-buttons .social-icon {
  font-size: 1rem;
}

.social-small.social-rounded {
  width: 2.5rem;
  height: 2.5rem;
  padding: 0.5rem;
  min-width: 2.5rem;
}

.social-small.social-rounded .social-icon {
  font-size: 1rem;
}

.social-medium.social-icons {
  font-size: 1.5rem;
  padding: 0.5rem;
}

.social-medium.social-buttons {
  padding: 0.75rem 1.25rem;
  font-size: 1rem;
}

.social-medium.social-buttons .social-icon {
  font-size: 1.1rem;
}

.social-medium.social-rounded {
  width: 3rem;
  height: 3rem;
  padding: 0.75rem;
  min-width: 3rem;
}

.social-medium.social-rounded .social-icon {
  font-size: 1.2rem;
}

.social-large.social-icons {
  font-size: 2rem;
  padding: 0.75rem;
}

.social-large.social-buttons {
  padding: 1rem 1.5rem;
  font-size: 1.125rem;
}

.social-large.social-buttons .social-icon {
  font-size: 1.25rem;
}

.social-large.social-rounded {
  width: 4rem;
  height: 4rem;
  padding: 1rem;
  min-width: 4rem;
}

.social-large.social-rounded .social-icon {
  font-size: 1.5rem;
}

/* Hover Effects */
.social-link:hover .social-icon {
  animation: socialBounce 0.6s ease;
}

@keyframes socialBounce {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

/* Platform Specific Colors on Hover */
.social-link[href*="facebook.com"]:hover {
  color: #1877f2 !important;
}

.social-link[href*="twitter.com"]:hover,
.social-link[href*="x.com"]:hover {
  color: #000000 !important;
}

.social-link[href*="instagram.com"]:hover {
  color: #E4405F !important;
}

.social-link[href*="linkedin.com"]:hover {
  color: #0077B5 !important;
}

.social-link[href*="youtube.com"]:hover {
  color: #FF0000 !important;
}

.social-link[href*="tiktok.com"]:hover {
  color: #000000 !important;
}

.social-link[href*="whatsapp"]:hover {
  color: #25D366 !important;
}

.social-link[href*="mailto"]:hover {
  color: #6c757d !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .social-links {
    gap: 0.5rem;
  }
  
  .social-large {
    font-size: 1.75rem !important;
  }
  
  .social-large.social-buttons {
    padding: 0.875rem 1.25rem !important;
    font-size: 1rem !important;
  }
  
  .social-large.social-rounded {
    width: 3.5rem !important;
    height: 3.5rem !important;
    min-width: 3.5rem !important;
  }
}

@media (max-width: 576px) {
  .social-links {
    justify-content: center !important;
  }
  
  .social-buttons .social-label {
    display: none;
  }
  
  .social-buttons {
    padding: 0.75rem !important;
    border-radius: 50% !important;
    width: 3rem !important;
    height: 3rem !important;
    min-width: 3rem !important;
    justify-content: center !important;
  }
}

/* Accessibility */
.social-link:focus {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

.social-link:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

/* Loading Animation */
.social-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.social-link:hover::before {
  left: 100%;
}
</style>